<template>
  <div class="p-m-3 sp-page">
    <MockBanner>The real form stays a popup; it is shown on a page here so it can be walked through.</MockBanner>
    <h2>New Enquiry</h2>
    <p class="sp-sub">
      Changed: the form starts with the <strong>Project</strong>, then the Description. The address comes from the Project's Site. For spare parts and
      small orders pick <strong>No project</strong> and fill the enquiry in as today.
    </p>
    <div class="sp-row" style="margin-bottom: 12px;">
      <span class="sp-muted">Try an example:</span>
      <Button label="S1 another contractor" class="p-button-sm p-button-outlined" @click="example('s1')" />
      <Button label="S2 same customer, other branch" class="p-button-sm p-button-outlined" @click="example('s2')" />
      <Button label="S3 another product type" class="p-button-sm p-button-outlined" @click="example('s3')" />
      <Button label="No project (spares)" class="p-button-sm p-button-outlined" @click="example('none')" />
      <Button label="Clear" class="p-button-sm p-button-text" @click="example('clear')" />
    </div>

    <div class="p-card p-p-4 p-fluid" style="max-width: 820px;">
      <h5 style="margin-top: 0;">Enquiry Details</h5>

      <!-- 1. Project first -->
      <div class="p-formgrid p-grid">
        <div class="p-field p-col-12">
          <label for="spEnqProject">Project *</label>
          <div class="p-inputgroup">
            <Dropdown
              id="spEnqProject"
              v-model="projectChoice"
              :options="projectOptions"
              optionLabel="label"
              optionValue="value"
              :filter="true"
              filterPlaceholder="Type project, site, street or postcode"
              placeholder="Pick the project, or No project"
            />
            <Button icon="pi pi-plus" label="New project" class="p-button-warning" style="width: auto;" @click="projectFormVisible = true" />
          </div>
          <small class="sp-muted">Suggestions while typing come from the site name, street and postcode (SP7, to be decided).</small>
        </div>
      </div>

      <div v-if="project" class="sp-note grey">
        <i class="pi pi-sitemap"></i>
        <span>
          <strong>{{ project.name }}</strong> at {{ site.name }}: {{ projectSummary }}
          <span class="sp-link" @click="$router.push('/sitesProjects/projects/' + project.id)"> Open project</span>
        </span>
      </div>

      <!-- 2. Description -->
      <div class="p-formgrid p-grid">
        <div class="p-field p-col-12">
          <label for="spEnqDesc">Description *</label>
          <InputText id="spEnqDesc" v-model.trim="description" style="text-transform: uppercase;" />
          <small v-if="project" class="sp-muted">Started from the site and project; add the detail of this enquiry.</small>
        </div>
      </div>

      <!-- Address: from the Site when there is a project, typed as today when there is not -->
      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Street</label>
          <InputText v-model="addr.address1" :disabled="!!project" :class="{ 'sp-readonly': project }" />
        </div>
        <div class="p-field p-col">
          <label>Sub Area</label>
          <InputText v-model="addr.address2" :disabled="!!project" :class="{ 'sp-readonly': project }" />
        </div>
        <div class="p-field p-col">
          <label>City/Town</label>
          <InputText v-model="addr.town" :disabled="!!project" :class="{ 'sp-readonly': project }" />
        </div>
        <div class="p-field p-col">
          <label>Post Code</label>
          <InputText v-model="addr.postCode" :disabled="!!project" :class="{ 'sp-readonly': project }" />
        </div>
      </div>
      <small v-if="project" class="sp-muted" style="display: block; margin: -8px 0 10px 0;">Address from the Site. Change it on the Site, once, for every enquiry there.</small>
      <div v-if="projectChoice === 'none' && description" class="sp-note info">
        <i class="pi pi-search"></i><span>No project: the duplicate-enquiry check runs on the description and address, as today.</span>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label for="spEnqCust">Customer *</label>
          <Dropdown id="spEnqCust" v-model="customer" :options="customerOptions" :filter="true" :editable="true" placeholder="Search for customer" />
        </div>
        <div class="p-field p-col">
          <label>Contact *</label>
          <Dropdown :options="[]" placeholder="Contacts of the customer" disabled />
        </div>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label for="spEnqRole">Customer role <span class="sp-muted">(new, for tenders)</span></label>
          <Dropdown id="spEnqRole" v-model="role" :options="['Main contractor', 'Sub-contractor', 'Client']" :showClear="true" placeholder="Optional" />
        </div>
        <div class="p-field p-col">
          <label for="spEnqBranch">Branch</label>
          <Dropdown id="spEnqBranch" v-model="branch" :options="branches" />
        </div>
        <div class="p-field p-col">
          <label for="spEnqPt">Product Type</label>
          <Dropdown id="spEnqPt" v-model="productType" :options="productTypes" :showClear="true" />
        </div>
      </div>

      <!-- Entry checks: S2 warning, S1 information, S3 nothing to warn about -->
      <div v-if="checks.s2" class="sp-note warn" style="display: block;">
        <div><i class="pi pi-exclamation-triangle"></i>
          <strong> Possible duplicate.</strong> {{ customer }} already has a live {{ productType }} quote on this project from
          <strong>{{ checks.s2.branch }}</strong>: <span class="sp-link">{{ checks.s2.quoteNo }}</span>, {{ money(checks.s2.value) }}, {{ checks.s2.created }}.
        </div>
        <div style="margin-top: 6px;">
          If you carry on, both are kept and marked as a duplicate pair. Only one counts in the quote bank (setting: the <strong>{{ s2RuleText }}</strong>), and
          {{ s2Warn }} get an email.
        </div>
        <div class="sp-row" style="margin-top: 8px;">
          <Button :label="'Open ' + checks.s2.quoteNo" class="p-button-sm" style="width: auto;" disabled />
          <Button label="Carry on and save" class="p-button-sm p-button-outlined p-button-danger" style="width: auto;" disabled />
        </div>
      </div>
      <div v-if="checks.s1.length" class="sp-note info">
        <i class="pi pi-info-circle"></i>
        <span>
          {{ checks.s1.length }} other contractor(s) are quoting {{ productType }} on this project: {{ checks.s1.map(e => e.customer).join(', ') }}.
          The project value uses the average of the quotes, so the job is counted once.
        </span>
      </div>
      <div v-if="checks.s3.length && !checks.s2" class="sp-note ok">
        <i class="pi pi-check"></i>
        <span>{{ customer }} also has {{ checks.s3.map(e => e.productType).join(', ') }} on this project. A different product type counts on its own: no warning.</span>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Enquiry Type *</label>
          <Dropdown v-model="enqType" :options="['OTP', 'Tender', 'Construction Tender']" />
        </div>
        <div class="p-field p-col">
          <label>Date Received *</label>
          <InputText :value="today" disabled />
        </div>
        <div class="p-field p-col">
          <label>Sales Exec</label>
          <InputText value="From the customer's branch account" disabled />
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col"><label>Requested By (optional)</label><InputText /></div>
        <div class="p-field p-col"><label>ABI No (optional)</label><InputText /></div>
        <div class="p-field p-col"><label>Customer Ref (optional)</label><InputText /></div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col"><label>Notes (Optional)</label><InputText /></div>
      </div>

      <div class="sp-row" style="justify-content: flex-end;">
        <Button label="Cancel" icon="pi pi-times" class="p-button-danger" style="width: 110px;" disabled />
        <Button label="Save" icon="pi pi-check" class="p-button-success" style="width: 110px;" disabled />
      </div>
    </div>

    <ProjectForm v-if="projectFormVisible" :visible="projectFormVisible" @close="projectFormVisible = false" @picked="onProjectPicked" />
  </div>
</template>

<script>
import MockBanner from './MockBanner.vue'
import ProjectForm from './ProjectForm.vue'
import {
  projects, projectById, siteById, projectLabel, enquiriesOfProject, productLines, isLive, customers,
  productTypes, branches, settings, money
} from './mockData'

const EXAMPLES = {
  s1: { project: 12, customer: 'RJC JOINERY LTD', role: 'Sub-contractor', branch: 'NORTH WEST', productType: 'Doorsets - STD', enqType: 'Construction Tender' },
  s2: { project: 41, customer: 'CARTER LAUREN', role: 'Main contractor', branch: 'DOORSETS', productType: 'Doorsets - BESPOKE', enqType: 'OTP' },
  s3: { project: 51, customer: 'KORI CONSTRUCTION LTD', role: 'Main contractor', branch: 'DOORSETS', productType: 'Steel Doorsets', enqType: 'OTP' },
  none: { project: 'none', customer: 'JEWSON LTD', role: null, branch: 'DOORSETS', productType: 'Spares', enqType: 'OTP', description: 'REPLACEMENT DOOR CLOSERS X 6', addr: { address1: 'Unit 4, Trafford Park', address2: '', town: 'Manchester', postCode: 'M17 1SN' } }
}

export default {
  name: 'SpNewEnquiryMock',
  components: { MockBanner, ProjectForm },
  data() {
    return {
      projectChoice: null,
      description: '',
      addr: { address1: '', address2: '', town: '', postCode: '' },
      customer: null,
      role: null,
      branch: 'DOORSETS',
      productType: null,
      enqType: 'OTP',
      projectFormVisible: false,
      version: 0,
      productTypes,
      branches,
      customerOptions: customers,
      today: new Date().toLocaleDateString('en-GB')
    }
  },
  computed: {
    projectOptions() {
      this.version
      return [{ value: 'none', label: '— No project (spare parts, small orders) —' }]
        .concat(projects.map(p => ({ value: p.id, label: projectLabel(p) })))
    },
    project() { return typeof this.projectChoice === 'number' ? projectById(this.projectChoice) : null },
    site() { return this.project && siteById(this.project.siteId) },
    projectSummary() {
      const lines = productLines(this.project.id).filter(l => l.productType[0] !== '(')
      if (!lines.length) return 'no enquiries yet.'
      return enquiriesOfProject(this.project.id).length + ' enquiries so far. ' + lines.map(l =>
        l.productType + ': ' + l.nCustomers + ' customer(s)' + (l.countedValue !== null ? ', counted ' + money(l.countedValue) : '')).join('; ') + '.'
    },
    live() { return this.project ? enquiriesOfProject(this.project.id).filter(isLive) : [] },
    checks() {
      const sameType = e => this.productType && e.productType === this.productType
      return {
        s2: this.customer && this.live.find(e => e.customer === this.customer && sameType(e)) || null, // any branch: usually another one
        s1: this.customer ? this.live.filter(e => e.customer !== this.customer && sameType(e)) : [],
        s3: this.customer ? this.live.filter(e => e.customer === this.customer && e.productType && !sameType(e)) : []
      }
    },
    s2RuleText() { return { latest: 'latest one', highest: 'highest one', first: 'first one entered' }[settings.s2Counts] },
    s2Warn() { return settings.s2Warn.join(' and ').toLowerCase() }
  },
  watch: {
    projectChoice() {
      if (this.project) {
        const s = this.site
        this.addr = { address1: s.address1, address2: s.address2, town: s.town, postCode: s.postCode }
        this.description = (s.name + ', ' + this.project.name).toUpperCase()
      } else if (this.projectChoice === 'none') {
        this.addr = { address1: '', address2: '', town: '', postCode: '' }
        this.description = ''
      }
    }
  },
  methods: {
    money,
    onProjectPicked(p) { this.projectFormVisible = false; this.version++; this.projectChoice = p.id },
    example(key) {
      if (key === 'clear') {
        Object.assign(this, { projectChoice: null, description: '', customer: null, role: null, productType: null, addr: { address1: '', address2: '', town: '', postCode: '' } })
        return
      }
      const x = EXAMPLES[key]
      this.projectChoice = x.project
      this.$nextTick(() => {
        Object.assign(this, { customer: x.customer, role: x.role, branch: x.branch, productType: x.productType, enqType: x.enqType })
        if (x.description) this.description = x.description
        if (x.addr) this.addr = { ...x.addr }
      })
    }
  }
}
</script>

<style scoped>
@import './sitesProjects.css';
</style>
